// Category gallery: header + (for Gästemedien) the uploader + windowed masonry +
// lightbox + multi-select ZIP download. Polls the listing every 20s with the
// revision ETag so live updates (video transcodes, other guests' uploads) appear
// without ever re-downloading an unchanged list.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api, categoryZipUrl, downloadZip, fetchMedia } from '../api.js';
import { Link } from '../router.jsx';
import { ERRORS, GALLERY } from '../text.js';
import Masonry from '../components/masonry.jsx';
import Lightbox from '../components/lightbox.jsx';
import Uploader from '../components/uploader.jsx';
import SelectBar from '../components/selectbar.jsx';
import { HeartIcon } from '../components/icons.jsx';
import { toast } from '../components/toast.jsx';

const POLL_MS = 20000;

export default function Gallery({ slug }) {
	const [category, setCategory] = useState(null);
	const [notFound, setNotFound] = useState(false);
	const [items, setItems] = useState(null);
	const [openIdx, setOpenIdx] = useState(null);
	const [selectMode, setSelectMode] = useState(false);
	const [selected, setSelected] = useState(() => new Set());
	const etagRef = useRef(null);

	useEffect(() => {
		let cancelled = false;
		api.categories()
			.then((cats) => {
				if (cancelled) return;
				const cat = cats.find((c) => c.id === slug);
				if (cat) setCategory(cat);
				else setNotFound(true);
			})
			.catch(() => toast('Die Galerie konnte nicht geladen werden.', 'error'));
		return () => {
			cancelled = true;
		};
	}, [slug]);

	useEffect(() => {
		let cancelled = false;
		etagRef.current = null;
		setItems(null);
		setSelected(new Set());
		setSelectMode(false);
		setOpenIdx(null);

		const load = async () => {
			if (document.hidden) return;
			try {
				const res = await fetchMedia(slug, etagRef.current);
				if (cancelled || res.notModified) return;
				etagRef.current = res.etag;
				setItems(res.items);
			} catch (err) {
				if (!cancelled && err.status === 404) setNotFound(true);
			}
		};
		load();
		const timer = setInterval(load, POLL_MS);
		return () => {
			cancelled = true;
			clearInterval(timer);
		};
	}, [slug]);

	// Guests never see failed items (server filters); processing tiles render as
	// placeholders. The lightbox navigates only what's actually viewable.
	const readyItems = useMemo(() => (items ?? []).filter((i) => i.status === 'ready'), [items]);
	const gridItems = useMemo(() => (items ?? []).filter((i) => i.status !== 'failed'), [items]);

	const refresh = async () => {
		try {
			const res = await fetchMedia(slug, null);
			etagRef.current = res.etag;
			setItems(res.items);
		} catch {
			/* next poll catches up */
		}
	};

	if (notFound) {
		return (
			<div className='gallerypage'>
				<div className='gallerypage__head'>
					<Link to='/' className='gallerypage__back'>
						← Alle Galerien
					</Link>
					<h1 className='gallerypage__title'>Nicht gefunden</h1>
					<p>Diese Galerie gibt es nicht (mehr).</p>
				</div>
			</div>
		);
	}

	const selectedBytes = readyItems.reduce((sum, i) => (selected.has(i.id) ? sum + i.size : sum), 0);

	const toggleSelect = (id) => {
		setSelected((cur) => {
			const next = new Set(cur);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	// Undo an accidental upload — the server only allows this for records this
	// device uploaded itself (see `mine` in the listing).
	const deleteOwn = async (item) => {
		if (!window.confirm(GALLERY.deleteConfirm)) return;
		try {
			await api.deleteMedia(item.id);
			toast(GALLERY.deleted, 'info');
			// Closing before the list shrinks to zero keeps the lightbox from
			// lingering invisibly with the body scroll still locked.
			if (readyItems.length <= 1) setOpenIdx(null);
			setItems((cur) => (cur ?? []).filter((i) => i.id !== item.id));
			refresh();
		} catch {
			toast(ERRORS.generic, 'error');
		}
	};

	const openFromGrid = (gridIndex) => {
		const item = gridItems[gridIndex];
		const idx = readyItems.indexOf(item);
		if (idx !== -1) setOpenIdx(idx);
	};

	return (
		<div className='gallerypage'>
			<div className='gallerypage__head'>
				<Link to='/' className='gallerypage__back'>
					← Alle Galerien
				</Link>
				<h1 className='gallerypage__title'>{category?.name ?? ' '}</h1>
				{category?.description && <p className='gallerypage__desc'>{category.description}</p>}
			</div>

			{category?.allowGuestUpload && <Uploader category={slug} onUploaded={refresh} />}

			{readyItems.length > 0 && (
				<div className='gallerypage__toolbar'>
					<span className='gallerypage__count'>{GALLERY.items(readyItems.length)}</span>
					<div className='gallerypage__actions'>
						<button type='button' className='btn btn--ghost gallerypage__toolbtn' onClick={() => setSelectMode((v) => !v)}>
							{selectMode ? GALLERY.selectDone : GALLERY.select}
						</button>
						<a className='btn btn--ghost gallerypage__toolbtn' href={categoryZipUrl(slug)}>
							{GALLERY.downloadAll}
						</a>
					</div>
				</div>
			)}

			{items !== null && gridItems.length === 0 && (
				<div className={`comingsoon${category?.allowGuestUpload ? ' comingsoon--slim' : ''}`}>
					<span className='comingsoon__heart' aria-hidden='true'>
						<HeartIcon size={44} />
					</span>
					{!category?.allowGuestUpload && <div className='comingsoon__badge'>{GALLERY.comingSoonBadge}</div>}
					<p className='comingsoon__text'>
						{category?.allowGuestUpload ? GALLERY.emptyUpload : (category?.comingSoonText ?? GALLERY.empty)}
					</p>
				</div>
			)}

			{gridItems.length > 0 && (
				<Masonry
					items={gridItems}
					selectMode={selectMode}
					selectedIds={selected}
					onToggleSelect={toggleSelect}
					onOpen={openFromGrid}
				/>
			)}

			{selectMode && (
				<SelectBar
					count={selected.size}
					bytes={selectedBytes}
					onSelectAll={() => setSelected(new Set(readyItems.map((i) => i.id)))}
					onDownload={() => downloadZip([...selected])}
					onDone={() => {
						setSelectMode(false);
						setSelected(new Set());
					}}
				/>
			)}

			<Lightbox items={readyItems} openIdx={openIdx} onClose={() => setOpenIdx(null)} onDelete={deleteOwn} />
		</div>
	);
}
